// Libs
import clsx from "clsx";

// Utils
import "./Checkbox.css";

interface CheckboxProps extends React.PropsWithChildren<unknown> {
    /**
     * Additional className for box
     */
    boxClassName?: string;
    /**
     * Additional props for box
     */
    boxProps?: Record<string, unknown>;
    /**
     * If checked
     */
    checked?: boolean;
    /**
     * Additional className for the checkbox
     */
    className?: string;
    /**
     * if it is disabled
     */
    disabled?: boolean;
    /**
     * if there is an error
     */
    hasError?: boolean;
    /**
     * Id for description element
     */
    id?: string;
    /**
     * Additional for input
     */
    inputClassName?: string;
    /**
     * className for label
     */
    labelClassName?: string;
    /**
     * className for sub label
     */
    subLabelClassName?: string;
    /**
     * Additional for input
     */
    inputProps?: Record<string, unknown>;
    /**
     * Label
     */
    label?: string;
    /**
     * Name of the input
     */
    name: string;
    /**
     * Click handler
     */
    onClick?: (event: any) => void;
    /**
     * Change handler
     */
    onChange: (event: any) => void;
    /**
     * Focus handler
     */
    onFocus?: (event: any) => void;
    /**
     * Blur handler
     */
    onBlur?: (event: any) => void;
    /**
     * If required
     */
    required?: boolean;
    /**
     * Sub label
     */
    subLabel?: string;
    /**
     * Value of the input
     */
    value?: string;
    /**
     * Define if the label should be highlighted with black color when isn't checked
     */
    highlightLabel?: boolean;
    /**
     * Custom content
     */
    CustomContent?: React.ReactNode;
    /**
     * Custom content
     */
    LabelEndAdornment?: React.ReactNode;
    /**
     * Custom content
     */
    LabelStartAdornment?: React.ReactNode;
}

export function Checkbox({
    boxProps,
    checked = false,
    disabled = false,
    hasError = false,
    id,
    labelClassName: labelClassNameProp,
    subLabelClassName: subLabelClassNameProp,
    inputProps,
    label,
    name,
    onClick,
    onChange,
    onFocus,
    onBlur,
    required = false,
    subLabel,
    value,
    highlightLabel = false,
    children,
    LabelEndAdornment,
    LabelStartAdornment,
    ...otherProps
}: CheckboxProps): JSX.Element {
    const rootClassName = clsx("fl-c-checkbox", {
        "fl-c-checkbox--error": hasError && !disabled,
        "fl-c-checkbox--checked": checked,
        "fl-c-checkbox--highlighted": highlightLabel && !checked && !disabled,
        "fl-c-checkbox-disabled-container": disabled,
    });
    const inputClassName = clsx("fl-c-checkbox__input");
    const boxClassName = clsx("fl-c-checkbox__box", {
        "fl-c-checkbox-disabled-box": disabled,
    });
    const labelClassName = clsx("fl-c-checkbox__label", labelClassNameProp);
    const subLabelClassName = clsx("fl-c-checkbox__sublabel", subLabelClassNameProp);

    return (
        <div {...otherProps} className={rootClassName} disabled={disabled}>
            <input
                {...inputProps}
                checked={checked}
                className={inputClassName}
                disabled={disabled}
                id={id}
                name={name}
                onClick={onClick}
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={onChange}
                required={required}
                type="checkbox"
                value={value}
            />
            <div {...boxProps} className={boxClassName}>
                {label && (
                    <div className={labelClassName}>
                        {LabelStartAdornment && LabelStartAdornment}
                        {label}
                        {LabelEndAdornment && LabelEndAdornment}
                    </div>
                )}
                {subLabel && <div className={subLabelClassName}>{subLabel}</div>}
                <div className={labelClassName}>{children}</div>
            </div>
        </div>
    );
}
